import React, { useEffect, useState } from 'react';
import ProjectService from '../services/ProjectService';
import PayInService from '../services/PayInService';
import PayoutService from '../services/PayoutService';
import PettyCashService from '../services/PettyCashService';
import ProjectScheduleService from '../services/ProjectScheduleService';
import ExpenditureTypeService from '../services/ExpenditureTypeService';
import utility from '../utils/utility';

const Dashboard = () => {
    const [projects, setProjects] = useState([]);
    const [payIn, setPayIn] = useState([]);
    const [payOut, setPayOut] = useState([]);
    const [pettyCash, setPettyCash] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const [projectsRes, payInRes, payOutRes, pettyCashRes, schedulesRes] = await Promise.all([
                ProjectService.getAll(1, 100),
                PayInService.getAllPayIn(1, 100),
                PayoutService.getAllPayouts(1, 100),
                PettyCashService.getAllPettyCash(1, 100),
                ProjectScheduleService.getAll(1, 100)
            ]);

            setProjects(projectsRes.data || []);
            setPayIn(payInRes.data || []);
            setPayOut(payOutRes.data || []);
            setPettyCash(pettyCashRes.data || []);
            setSchedules(schedulesRes.data || []);
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Calculate statistics
    const totalProjects = projects.length;
    const activeProjects = projects.filter(p => ['Preproduction', 'Production', 'PostProduction'].includes(p.status)).length;
    const completedProjects = projects.filter(p => ['Completed', 'Released'].includes(p.status)).length;
    const estimationProjects = projects.filter(p => p.status === 'Estimation').length;
    const cancelledProjects = projects.filter(p => p.status === 'Cancelled').length;
    const newProjects = projects.filter(p => p.status === 'New').length;

    const totalIncome = payIn.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const totalExpenditure = payOut.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const totalPettyCash = pettyCash.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

    const pendingSchedules = schedules.filter(s => s.status === 'pending').length;
    const upcomingSchedules = schedules.filter(s => new Date(s.startDate) > new Date()).length;

    const formatCurrency = (value) => Number(value || 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    const getStatusBadgeColor = (status) => {
        const statusColorMap = {
            'New': 'primary',
            'Estimation': 'info',
            'Preproduction': 'warning',
            'Production': 'success',
            'PostProduction': 'secondary',
            'Completed': 'success',
            'Released': 'info',
            'Cancelled': 'danger',
            'Aborted': 'danger'
        };
        return statusColorMap[status] || 'dark';
    };

    return (
        <div className="container-fluid py-4">
            <div className="row mb-4">
                <div className="col-12">
                    <h3 className="mb-0 h4 font-weight-bolder">Dashboard</h3>
                    <p className="text-sm text-muted">Project Management & Financial Overview</p>
                </div>
            </div>

            {/* Key Statistics Cards */}
            <div className="row mb-4">
                {/* Projects Overview */}
                <div className="col-lg-3 col-sm-6 mb-3">
                    <div className="card">
                        <div className="card-header p-3 pt-2">
                            <div className="icon icon-lg icon-shape bg-gradient-primary shadow text-center border-radius-lg">
                                <i className="material-symbols-rounded opacity-10">folder_open</i>
                            </div>
                        </div>
                        <div className="card-body pt-3">
                            <h5 className="font-weight-bold">Total Projects</h5>
                            <h3 className="text-primary mb-2">{totalProjects}</h3>
                            <p className="text-sm text-muted mb-0">
                                <span className="text-success font-weight-bold">{activeProjects}</span> Active |
                                <span className="text-info font-weight-bold ms-1">{completedProjects}</span> Completed |
                                <span className="text-warning font-weight-bold ms-1">{newProjects}</span> New
                            </p>
                        </div>
                    </div>
                </div>

                {/* Expenditure Overview */}
                <div className="col-lg-3 col-sm-6 mb-3">
                    <div className="card">
                        <div className="card-header p-3 pt-2">
                            <div className="icon icon-lg icon-shape bg-gradient-danger shadow text-center border-radius-lg">
                                <i className="material-symbols-rounded opacity-10">trending_down</i>
                            </div>
                        </div>
                        <div className="card-body pt-3">
                            <h5 className="font-weight-bold">Total Expenditure</h5>
                            <h3 className="text-danger mb-2">₹{formatCurrency(totalExpenditure)}</h3>
                            <p className="text-sm text-muted mb-0">
                                {payOut.length} transactions recorded
                            </p>
                        </div>
                    </div>
                </div>

                {/* Income Overview */}
                <div className="col-lg-3 col-sm-6 mb-3">
                    <div className="card">
                        <div className="card-header p-3 pt-2">
                            <div className="icon icon-lg icon-shape bg-gradient-success shadow text-center border-radius-lg">
                                <i className="material-symbols-rounded opacity-10">trending_up</i>
                            </div>
                        </div>
                        <div className="card-body pt-3">
                            <h5 className="font-weight-bold">Total Income</h5>
                            <h3 className="text-success mb-2">₹{formatCurrency(totalIncome)}</h3>
                            <p className="text-sm text-muted mb-0">
                                {payIn.length} transactions recorded
                            </p>
                        </div>
                    </div>
                </div>

                {/* Petty Cash Overview */}
                <div className="col-lg-3 col-sm-6 mb-3">
                    <div className="card">
                        <div className="card-header p-3 pt-2">
                            <div className="icon icon-lg icon-shape bg-gradient-warning shadow text-center border-radius-lg">
                                <i className="material-symbols-rounded opacity-10">payments</i>
                            </div>
                        </div>
                        <div className="card-body pt-3">
                            <h5 className="font-weight-bold">Petty Cash</h5>
                            <h3 className="text-warning mb-2">₹{formatCurrency(totalPettyCash)}</h3>
                            <p className="text-sm text-muted mb-0">
                                {pettyCash.length} transactions recorded
                            </p>
                        </div>
                    </div>
                </div>

                {/* Project Stage Overview */}
                <div className="col-lg-3 col-sm-6 mb-3">
                    <div className="card">
                        <div className="card-header p-3 pt-2">
                            <div className="icon icon-lg icon-shape bg-gradient-info shadow text-center border-radius-lg">
                                <i className="material-symbols-rounded opacity-10">layers</i>
                            </div>
                        </div>
                        <div className="card-body pt-3">
                            <h5 className="font-weight-bold">Project Stages</h5>
                            <div className="text-sm mb-2">
                                <div className="d-flex justify-content-between mb-1">
                                    <span>Pre-Prod:</span>
                                    <span className="font-weight-bold text-info">{projects.filter(p => p.status === 'Preproduction').length}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-1">
                                    <span>In-Prod:</span>
                                    <span className="font-weight-bold text-warning">{projects.filter(p => p.status === 'Production').length}</span>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <span>Post-Prod:</span>
                                    <span className="font-weight-bold text-success">{projects.filter(p => p.status === 'PostProduction').length}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Schedule & Financial Summary */}
            <div className="row mb-4">
                {/* Schedule Status */}
                <div className="col-lg-4 mb-4">
                    <div className="card">
                        <div className="card-header p-3">
                            <h6 className="mb-0 font-weight-bold">Schedule Status</h6>
                            <p className="text-sm text-muted mb-0">Project timeline overview</p>
                        </div>
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <p className="text-muted text-sm mb-0">Pending Tasks</p>
                                    <h5 className="font-weight-bold text-warning">{pendingSchedules}</h5>
                                </div>
                                <div className="icon icon-md icon-shape bg-gradient-warning text-center border-radius-lg">
                                    <i className="material-symbols-rounded opacity-10">schedule</i>
                                </div>
                            </div>
                            <hr className="horizontal my-2" />
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="text-muted text-sm mb-0">Upcoming Schedules</p>
                                    <h5 className="font-weight-bold text-info">{upcomingSchedules}</h5>
                                </div>
                                <div className="icon icon-md icon-shape bg-gradient-info text-center border-radius-lg">
                                    <i className="material-symbols-rounded opacity-10">event_upcoming</i>
                                </div>
                            </div>
                            <hr className="horizontal my-2" />
                            <div>
                                <p className="text-muted text-sm mb-2">Total Schedules</p>
                                <div className="progress">
                                    <div className="progress-bar bg-gradient-primary" role="progressbar" style={{ width: `${Math.min((schedules.length / 50) * 100, 100)}%` }} aria-valuenow={schedules.length} aria-valuemin="0" aria-valuemax="50"></div>
                                </div>
                                <p className="text-xs text-muted mt-1">{schedules.length} schedules</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Financial Summary */}
                <div className="col-lg-8 mb-4">
                    <div className="card">
                        <div className="card-header p-3">
                            <h6 className="mb-0 font-weight-bold">Financial Summary</h6>
                            <p className="text-sm text-muted mb-0">Income vs Expenditure analysis</p>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <p className="text-muted text-sm mb-1">Total Income</p>
                                            <h4 className="text-success font-weight-bold">₹{formatCurrency(totalIncome)}</h4>
                                        </div>
                                        <span className="badge bg-gradient-success">+{payIn.length}</span>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <p className="text-muted text-sm mb-1">Total Expenditure</p>
                                            <h4 className="text-danger font-weight-bold">₹{formatCurrency(totalExpenditure)}</h4>
                                        </div>
                                        <span className="badge bg-gradient-danger">+{payOut.length}</span>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <p className="text-muted text-sm mb-1">Petty Cash Used</p>
                                            <h4 className="text-warning font-weight-bold">₹{formatCurrency(totalPettyCash)}</h4>
                                        </div>
                                        <span className="badge bg-gradient-warning">+{pettyCash.length}</span>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <p className="text-muted text-sm mb-1">Net Balance</p>
                                            <h4 className={`font-weight-bold ${totalIncome - totalExpenditure - totalPettyCash >= 0 ? 'text-success' : 'text-danger'}`}>
                                                ₹{formatCurrency(totalIncome - totalExpenditure - totalPettyCash)}
                                            </h4>
                                        </div>
                                        <i className="material-symbols-rounded text-lg">account_balance</i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Current Running Projects */}
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header pb-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 className="mb-0 font-weight-bold">Current Running Projects</h6>
                                    <p className="text-sm text-muted mb-0">Active projects status</p>
                                </div>
                                <span className="badge bg-gradient-primary">{activeProjects} Active</span>
                            </div>
                        </div>
                        <div className="card-body px-0 pb-3">
                            {loading ? (
                                <div className="text-center p-4">Loading...</div>
                            ) : activeProjects === 0 ? (
                                <div className="text-center p-4 text-muted">No active projects at the moment</div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-items-center mb-0">
                                        <thead>
                                            <tr>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-3">Project Name</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Est. Cost</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Status</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Progress</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {projects.filter(p => ['Preproduction', 'Production', 'PostProduction'].includes(p.status)).slice(0, 10).map((project) => (
                                                <tr key={project.id}>
                                                    <td className="ps-3">
                                                        <div>
                                                            <h6 className="mb-0 text-sm font-weight-bold">{project.name}</h6>
                                                            <p className="text-xs text-muted mb-0">{project.description || 'No description'}</p>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <p className="text-sm font-weight-bold mb-0">₹{formatCurrency(project.estimatedProjectCost || 0)}</p>
                                                    </td>
                                                    <td>
                                                        <span className={`badge bg-gradient-${getStatusBadgeColor(project.status)}`}>
                                                            {project.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="progress" style={{ height: '5px' }}>
                                                            <div className="progress-bar bg-gradient-primary" role="progressbar" style={{ width: '45%' }} aria-valuenow="45" aria-valuemin="0" aria-valuemax="100"></div>
                                                        </div>
                                                        <p className="text-xs text-muted mb-0 mt-1">45%</p>
                                                    </td>
                                                    <td className="text-center">
                                                        <a href={`/project-details/${project.id}`} className="btn btn-link text-info mb-0">
                                                            <i className="material-symbols-rounded">visibility</i>
                                                        </a>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;

